
export const CANONICAL_ROLE = {
  REPORTING_MANAGER: "Manager",
  FINANCE_MANAGER: "Finance_Manager",
};

/**
 * getCanonicalRoles() already lower-cases and trims. Separators are folded on
 * top of that so a role seeded as "Finance_Manager", "Finance Manager" or
 * "finance-manager" is one and the same role — the tenant's spelling of a
 * separator must never decide whether a page opens.
 */
function normalizeRole(role) {
  return String(role ?? "").trim().toLowerCase().replace(/[\s_-]+/g, "_");
}

/**
 * Exact match, or a qualified variant of the same role ("Manager - Sales").
 * Deliberately not a substring test: "finance_manager" must not satisfy the
 * Manager gate, or Finance would inherit the reportee queue.
 */
function hasRole(roles, target) {
  const wanted = normalizeRole(target);
  return (roles || []).some((role) => {
    const value = normalizeRole(role);
    return value === wanted || value.startsWith(`${wanted}_`);
  });
}

/** Reporting Manager — Screen 7, and Screen 6 for their reportees' claims. */
export function isReportingManagerRole(roles = []) {
  return hasRole(roles, CANONICAL_ROLE.REPORTING_MANAGER);
}

/** Finance Team member — Screen 8, and Screen 6 at the Finance step. */
export function isFinanceManagerRole(roles = []) {
  return hasRole(roles, CANONICAL_ROLE.FINANCE_MANAGER);
}

/** Either role: the two screens that both approver personas can open. */
export function isApproverRole(roles = []) {
  return isReportingManagerRole(roles) || isFinanceManagerRole(roles);
}

/* ---------------------------------------------------------------- actions */

/**
 * The three actions Screen 6 offers, plus the Finance-only status flip on
 * Screen 8. These are UI identifiers: the wire values belong to the approval
 * endpoint, which has no controller yet, so the mapping from these to whatever
 * the API names them is made in one place — lib/approvalDataSource.
 */
export const APPROVER_ACTION = {
  APPROVE: "approve",
  REJECT: "reject",
  SEND_BACK: "sendBack",
  MARK_PROCESSED: "markProcessed",
};

/**
 * Reject and Send Back always carry a reason; Approve carries one only if the
 * approver chose to add it (PRD Screen 6, VR-22).
 */
export const ACTION_REQUIRES_REASON = {
  [APPROVER_ACTION.APPROVE]: false,
  [APPROVER_ACTION.REJECT]: true,
  [APPROVER_ACTION.SEND_BACK]: true,
  [APPROVER_ACTION.MARK_PROCESSED]: false,
};

export const ACTION_LABEL_KEY = {
  [APPROVER_ACTION.APPROVE]: "conveyanceApproval.approve",
  [APPROVER_ACTION.REJECT]: "conveyanceApproval.reject",
  [APPROVER_ACTION.SEND_BACK]: "conveyanceApproval.sendBack",
  [APPROVER_ACTION.MARK_PROCESSED]: "conveyanceApproval.markProcessed",
};

/** Audit-trail rows render whatever action the API recorded, via this map. */
export const AUDIT_ACTION_LABEL_KEY = {
  Submit: "conveyanceApproval.auditSubmit",
  Resubmit: "conveyanceApproval.auditResubmit",
  Approve: "conveyanceApproval.approve",
  Reject: "conveyanceApproval.reject",
  Send_back: "conveyanceApproval.sendBack",
  Mark_processed: "conveyanceApproval.markProcessed",
};

/* ----------------------------------------------------------------- routes */

export const TEAM_CLAIMS_PATH = "/hrms-team/conveyance-claims";
export const ALL_CLAIMS_PATH = "/hrms-team/conveyance-claims/all";

export function claimDetailPath(claimId, { from } = {}) {
  const base = `${TEAM_CLAIMS_PATH}/${claimId}`;
  return from ? `${base}?from=${encodeURIComponent(from)}` : base;
}

/** Where Screen 6's back link returns to, per the list that opened it. */
export const DETAIL_ORIGIN = {
  TEAM: "team",
  ALL: "all",
};
